import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { WorkflowStatusNoteDialog } from '@/components/jobs/workflow-status-note-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import {
  getWorkflowTransitionLabel,
  workflowEntityLabel,
  workflowTransitionRequiresNote,
  workflowTransitionRequiresReason,
} from '@/lib/workflow-status-note'
import jobWorkflowApi from '@/services/job-workflow'
import type { JobWorkflowEntityType, JobWorkflowTransition } from '@/types'

type WorkflowStatusSelectProps = {
  entityType: JobWorkflowEntityType
  entityId: string | number
  currentValue?: string | null
  disabled?: boolean
  className?: string
  contextDescription?: string
  onApplied?: () => void
  onSelectTransition?: (transition: JobWorkflowTransition, note?: string) => void
}

const cancellationReasonEntityType = (entityType: JobWorkflowEntityType) => {
  if (entityType === 'job') return 'job'
  if (entityType === 'assignment') return 'assignment'
  if (entityType === 'workday_entry') return 'workday_entry'
  return null
}

type PendingNoteDialogState = {
  transition: JobWorkflowTransition
  isReason: boolean
  note: string
}

export function WorkflowStatusSelect({
  entityType,
  entityId,
  currentValue,
  disabled = false,
  className,
  contextDescription,
  onApplied,
  onSelectTransition,
}: WorkflowStatusSelectProps) {
  const queryClient = useQueryClient()
  const [pendingNoteDialog, setPendingNoteDialog] = useState<PendingNoteDialogState | null>(null)

  const transitionsQuery = useQuery({
    queryKey: ['job-workflow-transitions', entityType, String(entityId)],
    queryFn: () => jobWorkflowApi.runtime.getStatusTransitions(entityType, entityId),
    enabled: Boolean(entityId),
  })
  const reasonEntityType = cancellationReasonEntityType(entityType)
  const cancellationReasonsQuery = useQuery({
    queryKey: ['job-cancellation-reasons', reasonEntityType, String(entityId)],
    queryFn: () => jobWorkflowApi.runtime.getCancellationReasons(reasonEntityType!, { entity_id: entityId }),
    enabled: Boolean(entityId) && reasonEntityType !== null,
  })

  const applyMutation = useMutation({
    mutationFn: ({ transition, note }: { transition: JobWorkflowTransition; note?: string }) => jobWorkflowApi.runtime.applyStatus({
      entity_type: entityType,
      entity_id: entityId,
      to_status: transition.to_status,
      note,
    }),
    onSuccess: async () => {
      toast.success('Status updated')
      await queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', entityType, String(entityId)] })
      await queryClient.refetchQueries({ queryKey: ['job-workflow-transitions', entityType, String(entityId)], type: 'active' })

      if (entityType === 'assignment') {
        queryClient.invalidateQueries({ queryKey: ['assignment', String(entityId)] })
        queryClient.invalidateQueries({ queryKey: ['assignments'] })
        queryClient.invalidateQueries({ queryKey: ['job-assignments'] })
        queryClient.invalidateQueries({ queryKey: ['worker-assignments'] })
      }

      if (entityType === 'workday_entry') {
        queryClient.invalidateQueries({ queryKey: ['workday-entry', String(entityId)] })
        queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
        queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
        queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
        queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      }

      if (entityType === 'job') {
        queryClient.invalidateQueries({ queryKey: ['job', String(entityId)] })
        queryClient.invalidateQueries({ queryKey: ['jobs'] })
      }

      onApplied?.()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const transitions = transitionsQuery.data ?? []
  const isBusy = transitionsQuery.isLoading || applyMutation.isPending
  const selectDisabled = disabled || isBusy || transitions.length === 0
  const currentLabel = currentValue?.trim() || null
  const placeholder = transitionsQuery.isLoading
    ? 'Loading statuses...'
    : transitions.length > 0
      ? currentLabel || 'Change status'
      : currentLabel || 'No status actions'

  const completeTransition = (transition: JobWorkflowTransition, note?: string) => {
    if (onSelectTransition) {
      onSelectTransition(transition, note)
      return
    }
    applyMutation.mutate({ transition, note })
  }

  const handleTransitionSelected = (transition: JobWorkflowTransition) => {
    if (workflowTransitionRequiresNote(transition)) {
      setPendingNoteDialog({
        transition,
        isReason: workflowTransitionRequiresReason(transition),
        note: '',
      })
      return
    }
    completeTransition(transition)
  }

  const handleNoteDialogConfirm = () => {
    if (!pendingNoteDialog) {
      return
    }

    const trimmedNote = pendingNoteDialog.note.trim()
    if (pendingNoteDialog.isReason && !trimmedNote) {
      toast.error('Reason is required for this status change')
      return
    }

    const { transition } = pendingNoteDialog
    setPendingNoteDialog(null)
    completeTransition(transition, trimmedNote || undefined)
  }

  return (
    <>
      <Select
        value=""
        disabled={selectDisabled}
        onValueChange={(value) => {
          const transition = transitions.find((item) => String(item.to_status) === value)
          if (!transition) {
            return
          }
          handleTransitionSelected(transition)
        }}
      >
        <SelectTrigger className={className}>
          {isBusy ? <Spinner className="mr-2 h-4 w-4" /> : null}
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {transitions.map((transition) => (
            <SelectItem key={transition.id} value={String(transition.to_status)}>
              {getWorkflowTransitionLabel(transition)}
              {transition.to_status_reason_required ? ' (reason)' : ''}
              {transition.requires_note ? ' (note)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <WorkflowStatusNoteDialog
        open={Boolean(pendingNoteDialog)}
        onOpenChange={(open) => {
          if (!open && !applyMutation.isPending) {
            setPendingNoteDialog(null)
          }
        }}
        targetLabel={pendingNoteDialog ? getWorkflowTransitionLabel(pendingNoteDialog.transition) : ''}
        targetCode={pendingNoteDialog?.transition.to_status_code ?? null}
        isReason={Boolean(pendingNoteDialog?.isReason)}
        note={pendingNoteDialog?.note ?? ''}
        onNoteChange={(note) => {
          setPendingNoteDialog((current) => (current ? { ...current, note } : current))
        }}
        onConfirm={handleNoteDialogConfirm}
        isPending={applyMutation.isPending}
        contextDescription={contextDescription}
        entityLabel={workflowEntityLabel(entityType)}
        cancellationReasons={cancellationReasonsQuery.data ?? []}
      />
    </>
  )
}
